import React, { useState } from 'react';
import Input from '../../ui/Input';
import Button from '../../ui/Button';
import { useAuth } from '../../../hooks/useAuth';
import { useFirestore } from '../../../hooks/useFirestore';

interface CreateSecretaryProps {
  onSuccess: () => void;
  onCancel: () => void;
}

interface Institution {
  id: string;
  institutionId: string;
  name: string;
  city: string;
}

const CreateSecretary: React.FC<CreateSecretaryProps> = ({
  onSuccess,
  onCancel,
}) => {
  const { user } = useAuth();
  const { getDocuments } = useFirestore('institutions');
  const { addDocument } = useFirestore('users'); // Ajout pour créer l'utilisateur
  
  const [isLoading, setIsLoading] = useState(false);
  const [loadingInstitutions, setLoadingInstitutions] = useState(false);
  const [error, setError] = useState('');
  const [institutions, setInstitutions] = useState<Institution[]>([]);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    institutionId: '',
    sendInvitation: true,
  });

  React.useEffect(() => {
    fetchInstitutions();
  }, []);

  const fetchInstitutions = async () => {
    setLoadingInstitutions(true);
    try {
      const docs = await getDocuments({
        field: 'createdBy',
        operator: '==',
        value: user?.uid,
      });
      setInstitutions(docs as Institution[]);
    } catch (error) {
      console.error('Error fetching institutions:', error);
    } finally {
      setLoadingInstitutions(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const validateForm = () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.institutionId) {
      setError('Veuillez remplir tous les champs obligatoires');
      return false;
    }

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Email invalide');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Trouver l'institution sélectionnée
      const selectedInstitution = institutions.find(inst => 
        inst.institutionId === formData.institutionId
      );
      
      // Préparer les données du secrétaire
      const secretaryData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        fullName: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone || '',
        role: 'secretary',
        institutionId: formData.institutionId,
        institutionName: selectedInstitution?.name || '',
        status: 'pending', // Statut initial
        createdBy: user?.uid,
        createdByEmail: user?.email,
        createdByName: user?.displayName,
        createdAt: new Date(),
        updatedAt: new Date(),
        lastLogin: null,
        isActive: true,
        emailVerified: false,
        invitationSent: formData.sendInvitation,
        invitationSentAt: formData.sendInvitation ? new Date() : null,
        // Champs pour permissions (peuvent être étendus plus tard)
        permissions: {
          canManageStudents: true,
          canManageTransactions: true,
          canViewReports: true,
          canRequestWithdrawal: true
        }
      };

      // Enregistrer le secrétaire dans Firestore
      await addDocument(secretaryData);

      // Ici, vous pourriez ajouter l'envoi d'email d'invitation si nécessaire
      if (formData.sendInvitation) {
        // Code pour envoyer l'email d'invitation
        console.log(`Invitation email would be sent to: ${formData.email}`);
        // await sendInvitationEmail(formData.email, formData.institutionId);
      }

      // Attendre un peu pour que Firestore mette à jour les données
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Fermer le modal
      onSuccess();
      
      // Déclencher un événement pour actualiser le dashboard
      window.dispatchEvent(new CustomEvent('secretary-created'));
      
    } catch (err) {
      console.error('Error creating secretary:', err);
      setError('Une erreur est survenue lors de la création du secrétaire. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Input
            label="Prénom *"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            disabled={isLoading}
            placeholder="Prénom"
          />
        </div>
        <div>
          <Input
            label="Nom *"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            disabled={isLoading}
            placeholder="Nom"
          />
        </div>
      </div>

      <div>
        <Input
          label="Email *"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          disabled={isLoading}
          placeholder="secretary@institution.com"
        />
      </div>

      <div>
        <Input
          label="Téléphone"
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          disabled={isLoading}
          placeholder="+225 01 23 45 67 89"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Institution * {loadingInstitutions && <span className="text-gray-400">(Chargement...)</span>}
        </label>
        <select
          name="institutionId"
          value={formData.institutionId}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          required
          disabled={isLoading || loadingInstitutions}
        >
          <option value="">Sélectionnez une institution</option>
          {institutions.map(inst => (
            <option key={inst.id} value={inst.institutionId}>
              {inst.name} ({inst.city})
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center">
        <input
          id="sendInvitation"
          name="sendInvitation"
          type="checkbox"
          checked={formData.sendInvitation}
          onChange={handleChange}
          disabled={isLoading}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="sendInvitation" className="ml-2 block text-sm text-gray-900">
          Envoyer une invitation par email
        </label>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          <strong>Note :</strong> Le secrétaire recevra un email avec les instructions pour compléter son inscription et configurer son mot de passe.
        </p>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
        >
          Annuler
        </Button>
        <Button
          type="submit"
          variant="primary"
          isLoading={isLoading}
        >
          Créer le secrétaire
        </Button>
      </div>
    </form>
  );
};

export default CreateSecretary;